import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "@/core/store/store";

const getBaseUrl = () => {
  // if (typeof window === "undefined") {
  //   return process.env.VITE_API_URL;
  // }
  // if (window.location.host === "localhost") {
  //   return "/api/v1";
  // }
  return import.meta.env.VITE_API_URL;
};

/** Хост курса для dev/preview: бэкенд ожидает тот же идентификатор, что на проде, а не localhost. */
const DEFAULT_DEV_COURSE_HOST = "salary.course.skillslab.center";

/**
 * Получить токен для авторизации
 * Приоритет: URL параметр > SCORM localStorage > Inline localStorage > Redux store
 */
/** Страница установки пароля по ссылке из письма (pages/set-password). */
const SET_PASSWORD_PATH = "/set_password";

const getAuthToken = (getState: () => unknown): { token: string; prefix: string } | null => {
  if (typeof window === "undefined") {
    const state = getState() as RootState;
    return state.auth.token ? { token: state.auth.token, prefix: "Bearer" } : null;
  }

  // 1. Проверяем токен в URL (для SCORM).
  // На /set_password в ?token= — одноразовый токен установки пароля users_service, не токен курса.
  const urlParams = new URLSearchParams(window.location.search);
  const tokenFromUrl =
    window.location.pathname === SET_PASSWORD_PATH ? null : urlParams.get("token");

  if (tokenFromUrl) {
    // Определяем тип токена по localStorage
    const scormToken = localStorage.getItem("scormAuthToken");
    if (scormToken === tokenFromUrl) {
      return { token: tokenFromUrl, prefix: "inline" };
    }
    // Может быть inline токен в URL
    const inlineToken = localStorage.getItem("inline_token");
    if (inlineToken === tokenFromUrl) {
      return { token: tokenFromUrl, prefix: "inline" };
    }
    // По умолчанию считаем SCORM если токен в URL
    return { token: tokenFromUrl, prefix: "inline" };
  }

  // 2. Проверяем SCORM токен в localStorage
  const scormToken = localStorage.getItem("scormAuthToken");
  if (scormToken) {
    return { token: scormToken, prefix: "inline" };
  }

  // 3. Проверяем inline токен в localStorage
  const inlineToken = localStorage.getItem("inline_token");
  if (inlineToken) {
    return { token: inlineToken, prefix: "inline" };
  }

  // 4. Проверяем токен в Redux store
  const state = getState() as RootState;
  if (state.auth.token) {
    return { token: state.auth.token, prefix: "Bearer" };
  }

  return null;
};

export const SSL_ACCESS_REVOKED_EVENT = "ssl-access-revoked";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: getBaseUrl(),
  prepareHeaders: (headers, { endpoint, getState }) => {
    const authData = getAuthToken(getState);

    if (authData) {
      headers.set("Authorization", `${authData.prefix} ${authData.token}`);
    }

    // На клиенте — хост курса для бэкенда: в проде фактический origin, в dev фиксированный (не localhost)
    if (typeof window !== "undefined") {
      const host = import.meta.env.PROD
        ? window.location.host
        : DEFAULT_DEV_COURSE_HOST;
      headers.set("x-course-host", host);
      headers.set("course-host", host);
    }

    return headers;
  },
});

/** Код бэкенда: токен недействителен на SSL-курсе (устаревший локальный, отозванный и т.п.). */
const SSL_AUTH_INVALID_CODE = "ssl_auth_invalid";

/** Все ключи localStorage, где приложение хранит токены (Bearer, inline, SCORM). */
const STORED_TOKEN_KEYS = ["access_token", "inline_token", "scormAuthToken"];

/** Страницы входа: с них не редиректим (иначе цикл), только чистим токены. */
const AUTH_PATHS = ["/login", "/register", "/forgot-password", SET_PASSWORD_PATH];

let sslReloginRedirectStarted = false;

const getErrorCode = (data: unknown): string | undefined => {
  if (data && typeof data === "object" && "code" in data) {
    const code = (data as { code?: unknown }).code;
    return typeof code === "string" ? code : undefined;
  }
  return undefined;
};

/**
 * Токен недействителен на SSL-курсе: чистим все сохранённые токены и Redux-сессию.
 * Возвращает true, если нужно уйти на /login (мы не на странице входа).
 */
const clearStoredAuth = (dispatch: (action: { type: string }) => unknown): boolean => {
  // auth/logout — экшен слайса entities/session (shared не импортирует entities по FSD);
  // он обнуляет токен в Redux и удаляет access_token из localStorage.
  dispatch({ type: "auth/logout" });
  if (typeof window === "undefined") return false;
  for (const key of STORED_TOKEN_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      // localStorage может быть недоступен — Redux-сессия уже очищена
    }
  }
  return !AUTH_PATHS.includes(window.location.pathname);
};

const baseQueryWithAccessCheck: typeof rawBaseQuery = async (
  args,
  api,
  extraOptions
) => {
  let result = await rawBaseQuery(args, api, extraOptions);
  const code = getErrorCode(result.error?.data);

  if (result.error?.status === 401 && code === SSL_AUTH_INVALID_CODE) {
    const shouldRedirect = clearStoredAuth(api.dispatch);
    // GET повторяем уже анонимно: публичные данные (курс) должны загрузиться.
    // Мутации не повторяем — без токена они всё равно не пройдут.
    const method = typeof args === "string" ? "GET" : (args.method ?? "GET");
    if (method.toUpperCase() === "GET") {
      result = await rawBaseQuery(args, api, extraOptions);
    }
    if (shouldRedirect && !sslReloginRedirectStarted) {
      sslReloginRedirectStarted = true;
      window.location.assign("/login");
    }
    return result;
  }

  if (
    result.error?.status === 403 &&
    code === "ssl_access_revoked" &&
    typeof window !== "undefined"
  ) {
    window.dispatchEvent(new CustomEvent(SSL_ACCESS_REVOKED_EVENT));
  }
  return result;
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithAccessCheck,
  tagTypes: ["User", "Course", "Lessons", "Simulator", "CourseUser"],

  endpoints: () => ({}),
});
